import { UserOutlined } from '@ant-design/icons';
import { Avatar, Form, Input, Select, Spin } from 'antd';
import type { FormInstance, FormItemProps } from 'antd';
import React, { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import debounce from 'lodash/debounce';
import { useTreeDepartmentEmployee } from '@/hooks/useTreeDepartmentEmployee';
import { useLocalFilter } from '@/hooks/useOrganizationIndexedDBFilter';
import { useAuthStore } from '@/store/useAuthStore';
import { EmployeeAvatar, EmployeeInfo } from '@/components/common/ActionPlan/Common/EmployeeAvatar';
import CustomTooltip from '@/components/common/Tooltip';

export interface EmployeeAvatarSelectProps {
  name: string;
  mode?: 'single' | 'multiple';
  placeholder?: string;
  label?: string;
  width?: number | string;
  maxWidth?: number | string;
  className?: string;
  allowClear?: boolean;
  form?: FormInstance;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  showName?: boolean;
  phanHe?: string;
  disabled?: boolean;
  excludeIds?: string[];
  /** Multiple: cuộn ngang khi hover (single luôn tắt). */
  scrollable?: boolean;
  autoOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  rules?: FormItemProps['rules'];
  /** false = modal (căn trái), true = column bảng (căn giữa). */
  isColumn?: boolean;
}

type ValueChange = (value: string | string[]) => void;

const TAG_SIZE = 26;
const MISSING_USER_TOOLTIP = 'User không tồn tại';

const LABEL_STYLE: React.CSSProperties = { color: '#4B5563', fontSize: 14, whiteSpace: 'nowrap', marginRight: 8 };

const WRAPPER_STYLE = (
  width: number | string,
  maxWidth?: number | string,
  isMultiple?: boolean,
  isColumn?: boolean
): React.CSSProperties => ({
  width: isColumn || isMultiple ? '100%' : width,
  maxWidth,
  minWidth: isColumn || isMultiple ? 0 : undefined,
  marginLeft: 0,
  paddingLeft: 0,
});

const parseIds = (raw: string | string[] | null | undefined): string[] => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.filter((id): id is string => Boolean(id && String(id).trim()));
  const s = String(raw).trim();
  return s ? s.split(',').map((x) => x.trim()).filter(Boolean) : [];
};

const normalizeValue = (
  selectedValue: string | string[] | undefined,
  isMultiple: boolean
): string | string[] | undefined =>
  isMultiple
    ? Array.isArray(selectedValue)
      ? selectedValue
      : selectedValue
        ? parseIds(selectedValue)
        : []
    : selectedValue;

const deptOrPos = (id: string, load: (id: string, field: string) => string) =>
  id ? load(id, 'Ten') || load(id, 'ten') || '' : '';

const sortBySelectionOrder = (options: { value: string }[], ids: string[]) => {
  if (!ids.length) return options;
  const order = new Map(ids.map((id, i) => [id, i]));
  return [...options].sort(
    (a, b) => (order.has(a.value) ? order.get(a.value)! : 999999) - (order.has(b.value) ? order.get(b.value)! : 999999)
  );
};

const employeeTooltipHtml = (info: EmployeeInfo) =>
  [
    info.tenNhanVien ? `<strong>Tên:</strong> ${info.tenNhanVien}` : '',
    info.boPhan ? `<strong>Bộ phận:</strong> ${info.boPhan}` : '',
    info.chucVu ? `<strong>Chức vụ:</strong> ${info.chucVu}` : '',
  ].filter(Boolean).join('<br/>');

const multiTagStyle = (): React.CSSProperties => ({
  marginTop: 0,
  padding: 0,
  height: TAG_SIZE,
  minHeight: TAG_SIZE,
  width: TAG_SIZE,
  maxWidth: TAG_SIZE,
  lineHeight: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0,
});

const overlayPos = (showName: boolean, isColumn?: boolean): React.CSSProperties => ({
  position: 'absolute',
  left: showName ? 0 : isColumn ? '50%' : 0,
  top: '50%',
  transform: showName ? 'translateY(-50%)' : isColumn ? 'translate(-50%, -50%)' : 'translateY(-50%)',
});

const buildSelectClassName = (isMultiple: boolean, shouldScrollTags: boolean, isColumn?: boolean) => {
  const base = isMultiple ? 'ho-tro-select' : 'phu-trach-select';
  const parts = [base];
  if (isColumn) parts.push('cvda-table-cell-select-center');
  if (shouldScrollTags) parts.push('employee-avatar-select-scroll-hover');
  return parts.join(' ');
};

const singleSelectCss = (isColumn?: boolean) => `
  .phu-trach-select { width: 100% !important; }
  .phu-trach-select .ant-select-selector {
    border: none !important; box-shadow: none !important; min-width: 0 !important;
    padding-left: 0 !important; padding-right: 0 !important;
    display: flex !important; align-items: center !important;
    justify-content: ${isColumn ? 'center' : 'flex-start'} !important;
    background: transparent !important;
  }
  .phu-trach-select .ant-select-selection-search,
  .phu-trach-select .ant-select-selection-search-input { display: none !important; }
  .phu-trach-select .ant-select-selection-item,
  .phu-trach-select .ant-select-selection-placeholder { opacity: 0 !important; }
  .phu-trach-select input { pointer-events: none !important; cursor: pointer !important; }
  .phu-trach-select .ant-select-clear,
  .phu-trach-select .ant-select-selection-item-remove { display: none !important; }
  .phu-trach-select .ant-select-selector,
  .phu-trach-select:hover .ant-select-selector,
  .phu-trach-select:active .ant-select-selector,
  .phu-trach-select.ant-select-focused .ant-select-selector,
  .phu-trach-select.ant-select-open .ant-select-selector,
  .phu-trach-select.ant-select-outlined .ant-select-selector,
  .phu-trach-select.ant-select-outlined.ant-select-focused .ant-select-selector {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
`;

const multiSelectCss = (shouldScrollTags: boolean, isColumn?: boolean) => `
  .ho-tro-select.ant-select {
    height: ${TAG_SIZE}px !important;
    max-height: ${TAG_SIZE}px !important;
    width: 100% !important;
    min-width: 0 !important;
  }
  .ho-tro-select .ant-select-selector {
    min-height: ${TAG_SIZE}px !important;
    max-height: ${TAG_SIZE}px !important;
    height: ${TAG_SIZE}px !important;
    padding: 0 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: ${isColumn ? 'center' : 'flex-start'} !important;
    overflow: hidden !important;
  }
  .ho-tro-select .ant-select-selection-overflow {
    display: flex !important;
    flex-wrap: ${shouldScrollTags ? 'nowrap' : 'wrap'} !important;
    align-items: center !important;
    justify-content: ${isColumn ? 'center' : 'flex-start'} !important;
    gap: 2px !important;
    height: ${TAG_SIZE}px !important;
    min-height: ${TAG_SIZE}px !important;
    max-height: ${TAG_SIZE}px !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    overflow-x: ${shouldScrollTags ? 'auto' : 'hidden'} !important;
    overflow-y: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    scrollbar-width: ${shouldScrollTags ? 'none' : 'auto'} !important;
    -ms-overflow-style: ${shouldScrollTags ? 'none' : 'auto'} !important;
  }
  ${shouldScrollTags ? `
  .employee-avatar-select-scroll-hover .ho-tro-select .ant-select-selection-overflow,
  .cvda-table-cell-edit .employee-avatar-select-scroll-hover .ho-tro-select .ant-select-selection-overflow {
    overflow-x: auto !important;
    overflow-y: hidden !important;
    flex: 1 1 0 !important;
    min-width: 0 !important;
    width: 0 !important;
    max-width: 100% !important;
    justify-content: flex-start !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
  .employee-avatar-select-scroll-hover.is-column .ho-tro-select .ant-select-selection-overflow,
  .cvda-table-cell-edit .employee-avatar-select-scroll-hover.is-column .ho-tro-select .ant-select-selection-overflow {
    justify-content: center !important;
  }
  .employee-avatar-select-scroll-hover.is-column.is-scrolling .ho-tro-select .ant-select-selection-overflow,
  .cvda-table-cell-edit .employee-avatar-select-scroll-hover.is-column.is-scrolling .ho-tro-select .ant-select-selection-overflow {
    justify-content: flex-start !important;
  }
  .employee-avatar-select-scroll-hover .ho-tro-select .ant-select-selection-overflow::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  .cvda-table-cell-edit .employee-avatar-select-scroll-hover,
  .cvda-table-cell-edit .employee-avatar-select-scroll-hover .ho-tro-select,
  .cvda-table-cell-edit .employee-avatar-select-scroll-hover .ho-tro-select .ant-select-selector {
    overflow: hidden !important;
  }
  ` : ''}
  .ho-tro-select .ant-select-selection-search,
  .ho-tro-select .ant-select-selection-search-input {
    width: 0 !important;
    min-width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
  .ho-tro-select .ant-select-selection-overflow-item {
    flex-shrink: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
  }
  .ho-tro-select .ant-select-selection-item {
    margin: 0 !important;
    padding: 0 !important;
    min-height: ${TAG_SIZE}px !important;
    max-height: ${TAG_SIZE}px !important;
    height: ${TAG_SIZE}px !important;
    line-height: 0 !important;
    border: none !important;
    background: transparent !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
  }
  .ho-tro-select .ant-select-selection-item-remove,
  .ho-tro-select .ant-select-arrow { display: none !important; }
  .ho-tro-select .ant-select-selector,
  .ho-tro-select:hover .ant-select-selector,
  .ho-tro-select:active .ant-select-selector,
  .ho-tro-select.ant-select-focused .ant-select-selector,
  .ho-tro-select.ant-select-open .ant-select-selector,
  .ho-tro-select.ant-select-outlined .ant-select-selector,
  .ho-tro-select.ant-select-outlined.ant-select-focused .ant-select-selector,
  .ho-tro-select.ant-select-outlined.ant-select-open .ant-select-selector {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
`;

type ScrollMetrics = {
  scrollWidth: number;
  clientWidth: number;
  maxScroll: number;
  thumbWidth: number;
};

const MultiScrollHost: React.FC<{
  maxWidth?: number | string;
  isColumn?: boolean;
  itemCount: number;
  children: React.ReactNode;
}> = ({ maxWidth, isColumn, itemCount, children }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<ScrollMetrics>({ scrollWidth: 0, clientWidth: 0, maxScroll: 0, thumbWidth: 0 });
  const canScrollRef = useRef(false);
  const rafRef = useRef(0);
  const dragRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const [hovered, setHovered] = useState(false);
  const [canScroll, setCanScroll] = useState(false);

  const getOverflowEl = useCallback(
    () => hostRef.current?.querySelector('.ant-select-selection-overflow') as HTMLElement | null,
    []
  );

  const applyThumb = useCallback(() => {
    const el = getOverflowEl();
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const nextCanScroll = scrollWidth > clientWidth + 1;

    if (nextCanScroll !== canScrollRef.current) {
      canScrollRef.current = nextCanScroll;
      setCanScroll(nextCanScroll);
    }

    if (!nextCanScroll) return;

    const thumbWidth = Math.max(16, (clientWidth / scrollWidth) * clientWidth);
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const thumbLeft = maxScroll > 0 ? ((clientWidth - thumbWidth) * scrollLeft) / maxScroll : 0;

    metricsRef.current = { scrollWidth, clientWidth, maxScroll, thumbWidth };

    const thumb = thumbRef.current;
    const track = trackRef.current;
    if (!thumb || !track) return;

    if (isColumn) track.style.width = `${clientWidth}px`;
    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbLeft}px)`;
  }, [getOverflowEl, isColumn]);

  const scheduleThumb = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(applyThumb);
  }, [applyThumb]);

  useLayoutEffect(() => {
    let disposed = false;
    let attachRaf = 0;
    let cleanup: (() => void) | undefined;

    const attach = () => {
      if (disposed) return;
      const host = hostRef.current;
      const el = getOverflowEl();
      if (!host || !el) {
        attachRaf = requestAnimationFrame(attach);
        return;
      }

      const onWheel = (e: WheelEvent) => {
        if (el.scrollWidth <= el.clientWidth) return;
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (delta === 0) return;
        e.preventDefault();
        e.stopPropagation();
        el.scrollLeft += delta;
        applyThumb();
      };

      const onScroll = () => scheduleThumb();

      host.addEventListener('wheel', onWheel, { passive: false });
      el.addEventListener('scroll', onScroll, { passive: true });

      const ro = new ResizeObserver(scheduleThumb);
      ro.observe(el);
      ro.observe(host);

      const mo = new MutationObserver(scheduleThumb);
      mo.observe(el, { childList: true, subtree: true });

      applyThumb();

      cleanup = () => {
        host.removeEventListener('wheel', onWheel);
        el.removeEventListener('scroll', onScroll);
        ro.disconnect();
        mo.disconnect();
      };
    };

    attach();

    return () => {
      disposed = true;
      cancelAnimationFrame(attachRaf);
      cancelAnimationFrame(rafRef.current);
      cleanup?.();
    };
  }, [itemCount, children, getOverflowEl, applyThumb, scheduleThumb]);

  useLayoutEffect(() => {
    if (canScroll) scheduleThumb();
  }, [canScroll, hovered, scheduleThumb]);

  const scrollToTrackX = useCallback(
    (clientX: number, trackRect: DOMRect) => {
      const el = getOverflowEl();
      const { maxScroll, thumbWidth } = metricsRef.current;
      if (!el || maxScroll <= 0) return;
      const ratio = Math.min(
        1,
        Math.max(0, (clientX - trackRect.left - thumbWidth / 2) / Math.max(1, trackRect.width - thumbWidth))
      );
      el.scrollLeft = ratio * maxScroll;
      applyThumb();
    },
    [getOverflowEl, applyThumb]
  );

  const handleTrackMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    scrollToTrackX(e.clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = getOverflowEl();
    const track = trackRef.current;
    if (!el || !track) return;

    dragRef.current = { startX: e.clientX, startScrollLeft: el.scrollLeft };

    const onMove = (ev: MouseEvent) => {
      const drag = dragRef.current;
      const { maxScroll, thumbWidth } = metricsRef.current;
      if (!drag || maxScroll <= 0) return;
      const trackWidth = track.getBoundingClientRect().width;
      const delta = ev.clientX - drag.startX;
      const scrollDelta = (delta / Math.max(1, trackWidth - thumbWidth)) * maxScroll;
      el.scrollLeft = Math.min(maxScroll, Math.max(0, drag.startScrollLeft + scrollDelta));
      applyThumb();
    };

    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div
      ref={hostRef}
      className={[
        'employee-avatar-select-scroll-hover',
        isColumn ? 'is-column' : undefined,
        canScroll ? 'is-scrolling' : undefined,
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth,
        minWidth: 0,
        maxHeight: TAG_SIZE,
        height: TAG_SIZE,
        minHeight: TAG_SIZE ,
        overflow: 'hidden',
      }}
    >
      {children}
      {canScroll && (
        <div
          ref={trackRef}
          role="presentation"
          onMouseDown={handleTrackMouseDown}
          style={{
            position: 'absolute',
            left: isColumn ? '50%' : 0,
            transform: isColumn ? 'translateX(-50%)' : undefined,
            width: isColumn ? metricsRef.current.clientWidth || '100%' : '100%',
          
            zIndex: 10,
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? 'auto' : 'none',
            transition: 'opacity 0.12s ease',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 4,
              borderRadius: 999,
              background: 'rgba(210, 218, 226, 0.5)',
            }}
          />
          <div
            ref={thumbRef}
            onMouseDown={handleThumbMouseDown}
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              height: 4,
              borderRadius: 999,
              background: 'rgba(110, 125, 140, 0.9)',
              cursor: 'grab',
              willChange: 'transform',
            }}
          />
        </div>
      )}
    </div>
  );
};

const dropdownOptionCss = () => `
  .employee-avatar-select-dropdown .ant-select-item-option-content > div > span:first-child {
    flex-shrink: 0 !important;
    width: ${TAG_SIZE}px !important;
    height: ${TAG_SIZE}px !important;
    min-width: ${TAG_SIZE}px !important;
    min-height: ${TAG_SIZE}px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .employee-avatar-select-dropdown .employee-avatar-hover-transparent {
    flex-shrink: 0 !important;
  }
`;

/** Component chọn nhân viên — single / multiple */
export const EmployeeAvatarSelect: React.FC<EmployeeAvatarSelectProps> = ({
  name,
  mode = 'single',
  placeholder,
  label,
  width = 'fit-content',
  maxWidth,
  className = '',
  allowClear = false,
  form,
  value: controlledValue,
  onChange,
  showName = true,
  phanHe = 'bsc',
  disabled = false,
  excludeIds = [],
  scrollable = false,
  autoOpen = false,
  onOpenChange: onOpenChangeProp,
  rules,
  isColumn = false,
}) => {
  const isMultiple = mode === 'multiple';
  const shouldScrollTags = isMultiple && scrollable;
  const isControlled = controlledValue !== undefined && !form;
  const selectClassName = buildSelectClassName(isMultiple, shouldScrollTags, isColumn);

  const [selectOpen, setSelectOpen] = useState(autoOpen);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(autoOpen);
  const hasRequestedEmployeesRef = useRef(false);

  const { tenantID } = useAuthStore();
  const { ready, loadEmployee, loadDepartmentData, loadPositionData, filterEmployeeLabel } = useLocalFilter();
  const watchedValue = Form.useWatch(name, form);

  const selectedIdsForApi = useMemo(
    () => parseIds(form ? watchedValue : controlledValue),
    [form, watchedValue, controlledValue]
  );

  const {
    employeeOptions: rawEmployeeOptions,
    loading,
    loadAllEmployees,
    loadMoreEmployees,
    hasMoreEmployees,
    searchEmployees,
    employeeSnapshots,
  } = useTreeDepartmentEmployee({
    tenantID: tenantID || '',
    enabled: hasOpenedOnce,
    selectedEmployeeIds: selectedIdsForApi,
    nhanViens: selectedIdsForApi.length > 0 ? `,${selectedIdsForApi.join(',')},` : undefined,
    layKiemNghiem: true,
    phanHe,
  });

  const employeeOptions = useMemo(
    () =>
      rawEmployeeOptions.map((opt) => {
        const lbl = filterEmployeeLabel(opt.value);
        return { value: opt.value, label: lbl.label || opt.label, disabled: excludeIds.includes(opt.value) };
      }),
    [rawEmployeeOptions, filterEmployeeLabel, excludeIds]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        searchEmployees(query);
        if (query && hasMoreEmployees) loadMoreEmployees(query);
      }, 500),
    [searchEmployees, hasMoreEmployees, loadMoreEmployees]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  // Prefetch NV đã chọn để hiển thị avatar khi xem chi tiết (không cần mở dropdown).
  useEffect(() => {
    if (selectedIdsForApi.length > 0 && !hasOpenedOnce) {
      setHasOpenedOnce(true);
    }
  }, [selectedIdsForApi, hasOpenedOnce]);
  // Chỉ gọi API lần đầu khi dropdown mở (autoOpen hoặc onOpenChange) — không prefetch khi mount có value
  useEffect(() => {
    if (!tenantID || !hasOpenedOnce || hasRequestedEmployeesRef.current) return;
    hasRequestedEmployeesRef.current = true;
    void loadAllEmployees();
  }, [tenantID, hasOpenedOnce, loadAllEmployees]);

  const getEmployeeInfo = useCallback(
    (employeeId?: string): EmployeeInfo | null => {
      if (!employeeId || !ready) return null;
      const employee = loadEmployee(employeeId);
      if (!employee) return null;

      const tenNhanVien = employee.TenNhanVien || employee.tenNhanVien || '';
      const employeeLabel = filterEmployeeLabel(employeeId);
      const bpid = employee.BPID || employee.bpid || '';
      const cvid = employee.CVID || employee.cvid || '';

      return {
        id: employeeId,
        tenNhanVien: tenNhanVien || employeeLabel.label || '',
        maNhanVien: employee.MaNhanVien || employee.maNhanVien || '',
        hinh: employee.Hinh || employee.hinh || '',
        boPhan: deptOrPos(bpid, loadDepartmentData),
        chucVu: deptOrPos(cvid, loadPositionData),
        fullLabel: employeeLabel.label || tenNhanVien,
      };
    },
    [ready, loadEmployee, loadDepartmentData, loadPositionData, filterEmployeeLabel]
  );

  const resolveEmployeeDisplayInfo = useCallback(
    (employeeId?: string): EmployeeInfo | null => {
      if (!employeeId) return null;
      const fromCache = getEmployeeInfo(employeeId);
      const snap = employeeSnapshots[employeeId];

      if (fromCache) {
        if (!snap) return fromCache;
        return {
          ...fromCache,
          boPhan: fromCache.boPhan && String(fromCache.boPhan).trim()
            ? fromCache.boPhan
            : snap.tenBoPhan || fromCache.boPhan || '',
          chucVu: fromCache.chucVu && String(fromCache.chucVu).trim()
            ? fromCache.chucVu
            : snap.tenChucVu || fromCache.chucVu || '',
        };
      }
      if (snap) {
        return {
          id: snap.id,
          tenNhanVien: snap.tenNhanVien,
          maNhanVien: snap.maNhanVien || '',
          hinh: snap.hinh,
          boPhan: snap.tenBoPhan,
          chucVu: snap.tenChucVu,
          fullLabel: snap.tenNhanVien,
        };
      }
      // Có ID nhưng IndexedDB/API chưa sẵn — vẫn hiện avatar chữ, tránh icon User trống
      const fallbackName = String(employeeId).replace(/_nv$/i, '');
      return {
        id: employeeId,
        tenNhanVien: fallbackName,
        maNhanVien: '',
        hinh: '',
        boPhan: '',
        chucVu: '',
        fullLabel: fallbackName,
      };
    },
    [getEmployeeInfo, employeeSnapshots]
  );

  const ensureOpen = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (!hasOpenedOnce) setHasOpenedOnce(true);
      setSelectOpen(true);
    },
    [hasOpenedOnce]
  );

  const handleDropdownVisible = useCallback(
    (open: boolean) => {
      if (!disabled) {
        setSelectOpen(open);
        if (open) setHasOpenedOnce(true);
      }
      onOpenChangeProp?.(open);
    },
    [disabled, onOpenChangeProp]
  );

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, offsetHeight, scrollHeight } = e.target as HTMLElement;
      if (scrollHeight - scrollTop - offsetHeight <= 50 && hasMoreEmployees && !loading) {
        loadMoreEmployees();
      }
    },
    [hasMoreEmployees, loading, loadMoreEmployees]
  );

  const renderSelect = (selectedValue: string | string[] | undefined, onValueChange: ValueChange) => {
    const normalizedValue = normalizeValue(selectedValue, isMultiple);
    const hasValue = isMultiple
      ? Array.isArray(normalizedValue) && normalizedValue.length > 0
      : !!normalizedValue;

    const employeeInfo = isMultiple
      ? null
      : normalizedValue
        ? resolveEmployeeDisplayInfo(String(normalizedValue))
        : null;

    const selectedIds = isMultiple
      ? (normalizedValue as string[])
      : normalizedValue
        ? [String(normalizedValue)]
        : [];
    const sortedOptions = sortBySelectionOrder(employeeOptions, selectedIds);

    const renderDropdown = (menu: React.ReactNode) => {
      const hasData = sortedOptions.length > 0;
      return (
        <div>
          <div style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
            <Input
              placeholder="Tìm kiếm..."
              allowClear
              onChange={(e) => debouncedSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              style={{ border: 'none', boxShadow: 'none' }}
            />
          </div>
          {loading && !hasData ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>
              <Spin size="small" style={{ marginRight: 8 }} />Đang tải nhân viên...
            </div>
          ) : (
            <>
              {menu}
              {loading && hasData && (
                <div style={{ padding: 8, textAlign: 'center', color: '#666', borderTop: '1px solid #f0f0f0' }}>
                  <Spin size="small" style={{ marginRight: 6 }} />Đang tải thêm...
                </div>
              )}
            </>
          )}
        </div>
      );
    };

    const renderOption = (option: { value?: string | number; label?: React.ReactNode }) => {
      const id = String(option.value || '');
      const info = resolveEmployeeDisplayInfo(id);
      const excluded = excludeIds.includes(id);
      return (
        <div className="flex items-center gap-2 min-w-0 w-full">
          <span
            className="inline-flex flex-shrink-0 items-center justify-center"
            style={{ width: TAG_SIZE, height: TAG_SIZE, minWidth: TAG_SIZE, minHeight: TAG_SIZE }}
          >
            <EmployeeAvatar employeeInfo={info} size={TAG_SIZE} />
          </span>
          <div className={`flex flex-col min-w-0 ${isMultiple ? '' : 'flex-1'}`}>
            <span
              className="font-medium"
              style={excluded ? { textDecoration: 'line-through', color: '#ff4d4f', opacity: 0.6 } : {}}
            >
              {info?.tenNhanVien || option.label || id}
              {info?.maNhanVien ? ` (${info.maNhanVien})` : ''}
            </span>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              {[info?.boPhan, info?.chucVu].filter(Boolean).join(' - ')}
            </div>
          </div>
        </div>
      );
    };

    const renderTag = (props: { value?: string | number }) => {
      const id = String(props.value || '');
      const info = resolveEmployeeDisplayInfo(id);
      return (
        <span style={multiTagStyle()}>
          <EmployeeAvatar employeeInfo={info} size={TAG_SIZE} />
        </span>
      );
    };

    const clickable = { pointerEvents: 'auto' as const, cursor: 'pointer' as const };

    const selectContent = (
      <>
        <Select
          value={normalizedValue}
          variant="borderless"
          onMouseDown={(e) => {
            if (disabled) e.preventDefault();
          }}
          onChange={(value) => {
            onValueChange(value);
            if (!isMultiple) setSelectOpen(false);
            onChange?.(value);
          }}
          open={selectOpen}
          onOpenChange={handleDropdownVisible}
          popupMatchSelectWidth={false}
          popupClassName="employee-avatar-select-dropdown"
          showSearch={false}
          optionFilterProp="label"
          options={sortedOptions}
          mode={isMultiple ? 'multiple' : undefined}
          allowClear={allowClear}
          filterOption={false}
          suffixIcon={null}
          className={selectClassName}
          disabled={disabled}
          style={{ border: 'none', boxShadow: 'none', width: '100%' }}
          onPopupScroll={handlePopupScroll}
          optionRender={renderOption}
          dropdownRender={renderDropdown}
          tagRender={isMultiple ? renderTag : undefined}
        />

        {!isMultiple && (
          <>
            {employeeInfo ? (
              <div
                style={{
                  ...overlayPos(showName, isColumn),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  pointerEvents: 'none',
                  width: showName ? '100%' : 'fit-content',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                {!selectOpen ? (
                  <CustomTooltip value={employeeTooltipHtml(employeeInfo)}>
                    <div style={clickable} onClick={ensureOpen}>
                      <EmployeeAvatar employeeInfo={employeeInfo} size={TAG_SIZE} disableTooltip />
                    </div>
                  </CustomTooltip>
                ) : (
                  <div style={{ pointerEvents: 'auto' }}>
                    <EmployeeAvatar employeeInfo={employeeInfo} size={TAG_SIZE} disableTooltip />
                  </div>
                )}
                {showName && (
                  <span
                    style={{
                      color: 'rgba(0, 0, 0, 0.85)',
                      fontSize: 14,
                      flex: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...clickable,
                    }}
                    onClick={ensureOpen}
                  >
                    {employeeInfo.tenNhanVien}
                  </span>
                )}
              </div>
            ) : hasValue ? (
              <div
                style={{
                  ...overlayPos(showName, isColumn),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  pointerEvents: 'none',
                  width: showName ? '100%' : 'fit-content',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                {!selectOpen ? (
                  <CustomTooltip value={MISSING_USER_TOOLTIP}>
                    <div style={clickable} onClick={ensureOpen}>
                      <EmployeeAvatar employeeInfo={null} size={TAG_SIZE} disableTooltip />
                    </div>
                  </CustomTooltip>
                ) : (
                  <div style={{ pointerEvents: 'auto' }}>
                    <EmployeeAvatar employeeInfo={null} size={TAG_SIZE} disableTooltip />
                  </div>
                )}
                {showName && (
                  <span
                    style={{
                      color: 'rgba(0, 0, 0, 0.45)',
                      fontSize: 14,
                      flex: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...clickable,
                    }}
                    onClick={ensureOpen}
                  >
                    {MISSING_USER_TOOLTIP}
                  </span>
                )}
              </div>
            ) : (
              <div
                style={{
                  ...overlayPos(showName, isColumn),
                  pointerEvents: 'none',
                  zIndex: 1,
                  color: 'rgba(0, 0, 0, 0.25)',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: showName ? 6 : 0,
                }}
              >
                <Avatar size={TAG_SIZE} icon={<UserOutlined />} />
                {showName && (placeholder || '')}
              </div>
            )}
          </>
        )}

        {isMultiple && !hasValue && (
          <div
            style={{
              position: 'absolute',
              left: isColumn ? '50%' : 0,
              top: '50%',
              transform: isColumn ? 'translate(-50%, -50%)' : 'translateY(-50%)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              height: TAG_SIZE,
            }}
          >
            <Avatar size={TAG_SIZE} icon={<UserOutlined />} />
          </div>
        )}

        <style
          dangerouslySetInnerHTML={{
            __html: `${isMultiple ? multiSelectCss(shouldScrollTags, isColumn) : singleSelectCss(isColumn)}${dropdownOptionCss()}`,
          }}
        />
      </>
    );

    if (shouldScrollTags) {
      return (
        <MultiScrollHost
          maxWidth={maxWidth}
          isColumn={isColumn}
          itemCount={selectedIds.length}
        >
          {selectContent}
        </MultiScrollHost>
      );
    }

    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth,
          minWidth: isMultiple ? 0 : 40,
          height: isMultiple ? TAG_SIZE : TAG_SIZE,
          maxHeight: isMultiple ? TAG_SIZE : TAG_SIZE,
          overflow: !showName && !isMultiple ? 'hidden' : 'visible',
        }}
      >
        {selectContent}
      </div>
    );
  };

  const wrapper = (children: React.ReactNode) => (
    <div
      className={`flex items-center ${isColumn ? 'justify-center w-full min-w-0' : ''} ${className}`}
      style={WRAPPER_STYLE(width, maxWidth, isMultiple, isColumn)}
    >
      {label && <span style={LABEL_STYLE}>{label}</span>}
      {children}
    </div>
  );

  if (isControlled) {
    return wrapper(renderSelect(controlledValue, () => { }));
  }

  return wrapper(
    <Form.Item
      label=""
      name={name}
      rules={rules}
      style={{ marginBottom: 0, marginLeft: 0, paddingLeft: 0, ...(isMultiple ? { minWidth: 0, flex: 1 } : {}) }}
    >
      <Form.Item noStyle shouldUpdate={(prev, cur) => prev[name] !== cur[name]}>
        {({ getFieldValue, setFieldValue }) =>
          renderSelect(getFieldValue(name), (value) => setFieldValue(name, value))
        }
      </Form.Item>
    </Form.Item>
  );
};

export default EmployeeAvatarSelect;
